import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo/logo.png';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Productions', href: '#productions' },
  { label: 'Videos', href: '#videos' },
  { label: 'Services', href: '#services' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar({ showFanCorner }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('#home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href) => {
    setActiveLink(href);
    setMenuOpen(false);

    // Fan corner navigation
    if (href === '#/fancorner') {
      window.location.hash = '/fancorner';
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Home page navigation
    const currentHash = window.location.hash;
    if (currentHash.startsWith('#/fancorner') || currentHash.startsWith('#/service/')) {
      window.location.hash = '';
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goHome = () => {
    window.location.hash = '';
    setActiveLink('#home');
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isFanCorner = window.location.hash === '#/fancorner';

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-brand-black/95 backdrop-blur-md shadow-lg shadow-black/50 py-3 border-b border-brand-gold/10'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <motion.button
          onClick={goHome}
          className="flex items-center cursor-pointer"
          whileHover={{ scale: 1.03 }}
        >
          <img
            src={logo}
            alt="Magic Moon Studios"
            className="h-12 w-auto object-contain"
            style={{ filter: 'drop-shadow(0 0 8px rgba(243,112,33,0.4))' }}
          />
        </motion.button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {!isFanCorner && navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className={`nav-link text-sm ${activeLink === link.href ? 'text-brand-gold' : ''}`}
            >
              {link.label}
              {activeLink === link.href && (
                <motion.span
                  layoutId="activeBar"
                  className="absolute bottom-[-4px] left-0 w-full h-0.5 bg-gradient-to-r from-brand-orange to-brand-gold"
                  style={{ borderRadius: 2 }}
                />
              )}
            </button>
          ))}

          {/* Fan Corner special link */}
          <motion.button
            onClick={() => handleNav('#/fancorner')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className={`relative px-4 py-2 rounded-sm font-cinzel text-[11px] tracking-widest uppercase transition-all duration-300 ${
              isFanCorner
                ? 'text-brand-black'
                : 'text-brand-gold hover:text-white border border-brand-gold/40 hover:border-brand-gold'
            }`}
            style={isFanCorner ? { background: 'linear-gradient(135deg,#f37021,#D4AF37)' } : {}}
          >
            <span className="mr-1">⭐</span>
            Fan Corner
            {isFanCorner && (
              <motion.div
                layoutId="fanCornerBg"
                className="absolute inset-0 rounded-sm"
                style={{ background: 'linear-gradient(135deg,#f37021,#D4AF37)', zIndex: -1 }}
              />
            )}
          </motion.button>
        </div>

        {/* CTA */}
        <div className="hidden md:block">
          <button
            onClick={() => handleNav('#contact')}
            className="btn-royal px-6 py-2.5 text-xs rounded-sm"
          >
            Get In Touch
          </button>
        </div>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden flex flex-col gap-[5px] p-2"
          aria-label="Menu"
        >
          <motion.span
            animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 7 : 0 }}
            className="block w-6 h-0.5 bg-brand-gold"
          />
          <motion.span
            animate={{ opacity: menuOpen ? 0 : 1 }}
            className="block w-6 h-0.5 bg-brand-gold"
          />
          <motion.span
            animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -7 : 0 }}
            className="block w-6 h-0.5 bg-brand-gold"
          />
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-brand-black/98 backdrop-blur-md border-t border-brand-gold/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {!isFanCorner && navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className="nav-link text-left text-base"
                >
                  {link.label}
                </button>
              ))}

              {/* Fan Corner mobile */}
              <button
                onClick={() => handleNav('#/fancorner')}
                className="flex items-center gap-2 font-cinzel text-sm tracking-widest uppercase text-brand-gold hover:text-brand-orange transition-colors"
              >
                <span>⭐</span> Fan Corner
              </button>

              <button
                onClick={() => handleNav('#contact')}
                className="btn-royal px-6 py-3 text-xs rounded-sm w-full"
              >
                Get In Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
